import React from 'react';
import { Document, Page, View, Text, Image } from '@react-pdf/renderer';

import marketplaceLogo from '../../../../Assets/images/buyer-marketplace.png';

import { styles } from './styles';
import {
  TableRow,
  CompactTableCell,
  ProductImage,
  PDFBarcode,
} from './PDFComponents';
import {
  formatSampleTypeDisplay,
  getBorderColor,
  formatDateUTC,
  selectLogo,
} from './utils';
import { getImageUrlForPDF } from '../utils/imageUtils';

import { SampleTagResponse } from '../types';

const FULL_PAGE_STYLES = {
  fontSize: {
    header: 22,
    infoRow: 14,
    tableHeader: 11,
    tableContent: 14,
    barcode: 14,
    swatch: 14,
    swatchNote: 10,
  },
  spacing: {
    padding: 2,
    margin: 10,
    marginSmall: 6,
  },
  dimensions: {
    container: { width: '8.5in', height: '11in', padding: 20, border: 30 },
    productImage: { width: 140, height: 270, marginBottom: 10 },
    barcode: { width: 200, height: 50 },
    swatch: { height: 120, border: '2px' },
    spacer: { height: 200 },
  },
};

function FullPageInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={[
        styles.infoRow,
        { paddingVertical: FULL_PAGE_STYLES.spacing.padding },
      ]}
    >
      <Text
        style={[styles.label, { fontSize: FULL_PAGE_STYLES.fontSize.infoRow }]}
      >
        {label}:
      </Text>
      <Text
        style={[
          styles.labelData,
          { fontSize: FULL_PAGE_STYLES.fontSize.infoRow },
        ]}
      >
        {value}
      </Text>
    </View>
  );
}

function getFullPageLogoDimensions(division?: string) {
  const normalizedDivision = division?.toLowerCase().trim();
  if (normalizedDivision === "kid's") return { width: 200, height: 30 };
  if (normalizedDivision === "men's") return { width: 220, height: 30 };
  return { width: 340, height: 32 };
}

/**
 * Vendor Catalog First Sample page content component (without Document/Page wrappers)
 * Can be used in both individual and combined PDFs
 */
export function VendorCatalogSamplePageContent({
  tagData,
}: {
  tagData: SampleTagResponse;
}) {
  const sampleType = tagData?.type_of_sample_tag;
  const styleNumber = tagData?.style_number;
  const developmentType = tagData?.type_of_development;

  const productImageUrl = getImageUrlForPDF(
    tagData?.product_image,
    tagData?.productImageDataUrl,
  );

  const borderColor = getBorderColor(sampleType, developmentType);
  const containerStyle = {
    ...FULL_PAGE_STYLES.dimensions.container,
    backgroundColor: '#ffffff',
    border: `${FULL_PAGE_STYLES.dimensions.container.border}px solid ${borderColor}`,
  };
  const barcodeValue = tagData?.sample_tag_lpn || tagData?.id;

  return (
    <View style={containerStyle}>
      {/* Main Content: Two Column Layout */}
      <View
        style={[
          styles.mainContent,
          { marginBottom: FULL_PAGE_STYLES.spacing.margin },
        ]}
      >
        {/* Left Column - All Content */}
        <View style={styles.leftColumn}>
          {/* Logo and Date Header */}
          <View style={[styles.logoDateHeader, { gap: 8 }]}>
            <Image
              src={selectLogo(tagData?.division)}
              style={getFullPageLogoDimensions(tagData?.division)}
            />
            <Image src={marketplaceLogo} style={{ width: 36, height: 28 }} />
          </View>
          {/* Horizontal Line */}
          <View
            style={[
              styles.horizontalLine,
              { marginBottom: FULL_PAGE_STYLES.spacing.marginSmall },
            ]}
          />

          {/* Sample Type Header */}
          <Text
            style={[
              styles.sampleTypeHeader,
              { fontSize: FULL_PAGE_STYLES.fontSize.header },
            ]}
          >
            {formatSampleTypeDisplay(sampleType).toUpperCase()}
          </Text>

          {/* Product Details */}
          <FullPageInfoRow
            label="Vendor"
            value={tagData?.company_name || tagData?.vendor_legal_name || '-'}
          />
          <FullPageInfoRow label="Style #" value={styleNumber || '-'} />
          <FullPageInfoRow
            label="CC Lead Time"
            value={`${tagData?.custom_cut_lead_time || '-'} weeks (${formatDateUTC(tagData?.cc_lead_time_date) || '-'})`}
          />
          <FullPageInfoRow label="COO" value={tagData?.coo || '-'} />
          <FullPageInfoRow
            label="FN Exclusive"
            value={tagData?.fn_exclusive ? 'Yes' : 'No'}
          />
          <FullPageInfoRow
            label="Fit Sample"
            value={tagData?.fit_sample ? 'Yes' : 'No'}
          />

          {/* Cost and MOQ Table */}
          <View
            style={{
              marginTop: FULL_PAGE_STYLES.spacing.margin,
              marginBottom: FULL_PAGE_STYLES.spacing.margin,
            }}
          >
            <View style={styles.tableRow}>
              <CompactTableCell
                width="25%"
                textStyle={{
                  fontSize: FULL_PAGE_STYLES.fontSize.tableHeader,
                  fontWeight: 'normal',
                }}
              >
                {' '}
              </CompactTableCell>
              <CompactTableCell
                width="37.5%"
                textAlign="center"
                textStyle={{
                  fontSize: FULL_PAGE_STYLES.fontSize.tableHeader,
                  fontWeight: 'normal',
                }}
              >
                Cost
              </CompactTableCell>
            </View>

            <TableRow>
              <CompactTableCell
                width="25%"
                textStyle={{
                  fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                  fontWeight: 'normal',
                }}
              >
                JR
              </CompactTableCell>
              <CompactTableCell
                width="37.5%"
                textAlign="center"
                textStyle={{
                  fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                }}
              >
                ${tagData?.junior_cost}
              </CompactTableCell>
            </TableRow>

            <TableRow isLast>
              <CompactTableCell
                width="25%"
                textStyle={{
                  fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                  fontWeight: 'normal',
                }}
              >
                Plus
              </CompactTableCell>
              <CompactTableCell
                width="37.5%"
                textAlign="center"
                textStyle={{
                  fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                }}
              >
                {tagData?.plus_cost ? `$${tagData.plus_cost}` : '-'}
              </CompactTableCell>
            </TableRow>
          </View>
        </View>

        {/* Right Column - Photo and Barcode */}
        <View style={styles.rightColumn}>
          <ProductImage
            imageUrl={productImageUrl}
            width={FULL_PAGE_STYLES.dimensions.productImage.width}
            height={FULL_PAGE_STYLES.dimensions.productImage.height}
            marginBottom={FULL_PAGE_STYLES.dimensions.productImage.marginBottom}
            objectFit="cover"
          />
          <PDFBarcode
            value={barcodeValue}
            width={FULL_PAGE_STYLES.dimensions.barcode.width}
            height={FULL_PAGE_STYLES.dimensions.barcode.height}
            fontSize={FULL_PAGE_STYLES.fontSize.barcode}
          />
        </View>
      </View>

      {/* Bottom Section: Color Swatches and FN Internal */}
      <View style={{ flexDirection: 'row', marginTop: 5 }}>
        {/* Color Swatches Section */}
        <View style={{ flex: 1, marginRight: 5, marginTop: -20 }}>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 'semibold',
              marginBottom: 10,
            }}
          >
            Color Swatches
          </Text>

          {/* Grid of 6 swatch boxes */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {[
              'swatch1',
              'swatch2',
              'swatch3',
              'swatch4',
              'swatch5',
              'swatch6',
            ].map((swatchId) => (
              <View
                key={swatchId}
                style={{
                  width: '48%',
                  height: 120,
                  margin: '1%',
                  border: '2px dashed #9E9E9E',
                  backgroundColor: '#ffffff',
                }}
              />
            ))}
          </View>

          <Text style={{ fontSize: 10, marginTop: 80 }}>
            Add additional swatches to back of card
          </Text>
        </View>

        {/* Vertical divider line */}
        <View style={[styles.verticalDivider, { marginHorizontal: 20 }]} />

        {/* FN Internal Section */}
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Text
            style={[styles.internalHeader, { fontSize: 14, marginBottom: 10 }]}
          >
            FN INTERNAL ONLY
          </Text>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 'semibold',
              marginBottom: 20,
            }}
          >
            Colors & Units
          </Text>

          {/* Space for content */}
          <View style={{ height: 330 }} />

          {/* Horizontal line above Drop Off Date */}
          <View
            style={{
              borderBottomWidth: 1,
              borderBottomColor: '#000',
              marginBottom: 10,
              width: '100%',
            }}
          />
          <Text style={{ fontSize: 14, fontWeight: 'semibold' }}>
            Drop Off Date:
          </Text>
        </View>
      </View>
    </View>
  );
}

/**
 * Vendor Catalog First Sample PDF component (with Document/Page wrappers)
 * For individual PDF generation
 */
export function VendorCatalogSamplePDF({
  tagData,
}: {
  tagData: SampleTagResponse;
}) {
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <VendorCatalogSamplePageContent tagData={tagData} />
      </Page>
    </Document>
  );
}
